import { supabase } from './supabase';

export interface Cookbook {
  id: string;
  title: string;
  author: string;
  description: string;
  folder: string;
  pageCount: number;
  previewPageLimit: number;
  hasPreview: boolean;
  isFreePreview: boolean;
  version: number;
  sortOrder: number;
  coverImage: string;
}

interface ManifestCookbook {
  id: string;
  title: string;
  author: string;
  description: string;
  folder: string;
  pageCount: number;
  previewPageLimit: number;
  hasPreview: boolean;
  isFreePreview: boolean;
  version: number;
  sortOrder: number;
}

interface CookbooksManifest {
  cookbooks: ManifestCookbook[];
}

const BUCKET_NAME = 'cookbooks';
const SIGNED_URL_EXPIRY = 3600;

async function getSignedUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(BUCKET_NAME)
    .createSignedUrl(path, SIGNED_URL_EXPIRY);
  
  if (error) {
    console.error('Error creating signed URL:', error);
    return null;
  }
  
  return data.signedUrl;
}

export async function fetchCookbooks(): Promise<Cookbook[]> {
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .download('manifest.json');
    
    if (error) {
      console.error('Error fetching manifest:', error.message);
      return [];
    }
    
    const text = await data.text();
    const manifest: CookbooksManifest = JSON.parse(text);
    
    const cookbooksWithCovers = await Promise.all(
      manifest.cookbooks.map(async (cookbook) => {
        const coverUrl = await getSignedUrl(`${cookbook.folder}/cover.png`);
        return {
          ...cookbook,
          coverImage: coverUrl || '/placeholder-cookbook.png',
        };
      })
    );
    
    return cookbooksWithCovers.sort((a, b) => a.sortOrder - b.sortOrder);
  } catch (error) {
    console.error('Error fetching cookbooks:', error);
    return [];
  }
}

export async function fetchCookbookById(id: string): Promise<Cookbook | null> {
  const cookbooks = await fetchCookbooks();
  return cookbooks.find((cookbook) => cookbook.id === id) || null;
}

export async function getCookbookPdfUrl(cookbook: Cookbook, hasFullAccess: boolean): Promise<{ url: string; isPreview: boolean }> {
  const canAccessFull = hasFullAccess || cookbook.isFreePreview;
  const canAccessPreview = cookbook.hasPreview;

  if (!canAccessFull && !canAccessPreview) {
    throw new Error('Subscription required to access this cookbook');
  }

  const usePreview = !canAccessFull && canAccessPreview;
  const filename = usePreview ? 'preview.pdf' : 'full.pdf';
  const pdfPath = `${cookbook.folder}/${filename}`;
  
  const signedUrl = await getSignedUrl(pdfPath);
  
  if (!signedUrl) {
    throw new Error('Failed to load cookbook PDF');
  }
  
  return {
    url: signedUrl,
    isPreview: usePreview,
  };
}
